import React from 'react';
import { X, Instagram, MapPin, Users } from 'lucide-react';
import { FaTiktok } from 'react-icons/fa';
import { formatFollowers } from './PropuestaPreview';

const CHART_COLORS = ['#F4A261', '#457B9D', '#1B6B3A', '#FF6EC7', '#00F0FF', '#E63946'];
const TAG_COLORS = ['#FF6EC7', '#00F0FF', '#39FF14', '#F4A261', '#457B9D', '#E63946'];

// Parse "Mujeres: 79%, Hombres: 20%" into [{label, value, color}]
const parseSexo = (str) => {
  if (!str) return [];
  const parts = str.split(',').map(s => s.trim());
  return parts.map((part, i) => {
    const match = part.match(/(.+?):\s*([\d.]+)%/);
    if (match) return { label: match[1].trim(), value: parseFloat(match[2]), color: i === 0 ? '#FF6EC7' : '#457B9D' };
    return null;
  }).filter(Boolean);
};

// Parse "18-24 (45%), 25-34 (32%), 13-17 (14%)" into [{label, value, color}]
const parseEdades = (str) => {
  if (!str) return [];
  const parts = str.split(',').map(s => s.trim());
  return parts.map((part, i) => {
    const match1 = part.match(/([\d-]+)\s*\((\d+)%\)/);
    const match2 = part.match(/(\d+)%\s*\(([\d-]+)\)/);
    if (match1) return { label: match1[1], value: parseFloat(match1[2]), color: CHART_COLORS[i % CHART_COLORS.length] };
    if (match2) return { label: match2[2], value: parseFloat(match2[1]), color: CHART_COLORS[i % CHART_COLORS.length] };
    return null;
  }).filter(Boolean);
};

// CSS conic-gradient pie chart
const PieChart = ({ data, size = 90 }) => {
  if (!data || data.length === 0) return null;
  const total = data.reduce((sum, d) => sum + d.value, 0);
  let cumulative = 0;
  const segments = data.map(d => {
    const start = (cumulative / total) * 360;
    cumulative += d.value;
    const end = (cumulative / total) * 360;
    return `${d.color} ${start}deg ${end}deg`;
  });

  return (
    <div className="flex items-center gap-4">
      <div
        style={{
          width: `${size}px`,
          height: `${size}px`,
          borderRadius: '50%',
          background: `conic-gradient(${segments.join(', ')})`,
          flexShrink: 0,
        }}
      />
      <div className="flex flex-col gap-1.5">
        {data.map((d, i) => (
          <div key={i} className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: d.color }} />
            <span className="text-sm text-gray-300">{d.label}: <strong className="text-white">{d.value}%</strong></span>
          </div>
        ))}
      </div>
    </div>
  );
};

const TalentModal = ({ talento, onClose }) => {
  if (!talento) return null;

  const verticals = talento.vertical ? talento.vertical.split(',').map(v => v.trim()) : [];
  const paises = talento.paises ? talento.paises.split(',').map(p => p.trim()) : [];
  const sexoData = parseSexo(talento.sexo);
  const edadesData = parseEdades(talento.edades);

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="talent-modal-overlay"
    >
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />

      <div
        className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-3xl shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        style={{ animation: 'modalPop 0.3s ease-out', backgroundColor: '#111' }}
        data-testid="talent-modal"
      >
        <style>{`
          @keyframes modalPop {
            from { opacity: 0; transform: scale(0.9) translateY(20px); }
            to { opacity: 1; transform: scale(1) translateY(0); }
          }
        `}</style>

        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-20 w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center text-white hover:bg-white/30 transition-all"
          data-testid="talent-modal-close"
        >
          <X size={20} />
        </button>

        {/* Header with photo */}
        <div className="relative h-64 overflow-hidden rounded-t-3xl">
          <img
            src={talento.image}
            alt={talento.name}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#111] via-black/30 to-transparent" />
          <div className="absolute bottom-4 left-6 right-6">
            <h2 className="text-3xl font-bold text-white uppercase tracking-tight" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
              {talento.name}
            </h2>
            {verticals.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3">
                {verticals.map((v, i) => (
                  <span
                    key={i}
                    className="px-4 py-1.5 text-sm font-semibold rounded-full"
                    style={{
                      color: TAG_COLORS[i % TAG_COLORS.length],
                      backgroundColor: TAG_COLORS[i % TAG_COLORS.length] + '20',
                      border: `1px solid ${TAG_COLORS[i % TAG_COLORS.length]}40`,
                    }}
                  >
                    {v}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Description */}
          {talento.descripcion && (
            <p className="text-gray-300 text-base leading-relaxed" style={{ fontFamily: "'DM Sans', sans-serif" }}>
              {talento.descripcion}
            </p>
          )}

          {/* Followers - clickable */}
          {(talento.instagram_followers || talento.tiktok_followers) && (
            <div className="grid grid-cols-2 gap-3">
              {talento.instagram_followers && (
                <a
                  href={talento.instagram_url || '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-gradient-to-br from-pink-900/40 to-purple-900/40 border border-pink-500/30 rounded-xl p-4 text-center transition-all hover:scale-105 hover:border-pink-500/60"
                >
                  <Instagram size={22} className="mx-auto mb-1 text-pink-400" />
                  <p className="text-2xl font-bold text-white">{formatFollowers(talento.instagram_followers)}</p>
                  <p className="text-xs text-gray-400">Seguidores</p>
                </a>
              )}
              {talento.tiktok_followers && (
                <a
                  href={talento.tiktok_url || '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-gradient-to-br from-gray-800/60 to-gray-900/60 border border-gray-600/30 rounded-xl p-4 text-center transition-all hover:scale-105 hover:border-gray-500/60"
                >
                  <FaTiktok size={20} className="mx-auto mb-1 text-white" />
                  <p className="text-2xl font-bold text-white">{formatFollowers(talento.tiktok_followers)}</p>
                  <p className="text-xs text-gray-400">Seguidores</p>
                </a>
              )}
            </div>
          )}

          {/* Charts row */}
          {(sexoData.length > 0 || edadesData.length > 0) && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {sexoData.length > 0 && (
                <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-4">
                    <Users size={16} className="text-gray-400" />
                    <span className="text-sm font-bold text-gray-300 uppercase tracking-wider">Género</span>
                  </div>
                  <PieChart data={sexoData} size={80} />
                </div>
              )}
              {edadesData.length > 0 && (
                <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-4">
                    <span className="text-sm font-bold text-gray-300 uppercase tracking-wider">Edades</span>
                  </div>
                  <PieChart data={edadesData} size={80} />
                </div>
              )}
            </div>
          )}

          {/* Countries */}
          {paises.length > 0 && (
            <div className="bg-white/5 border border-white/10 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-3">
                <MapPin size={16} className="text-gray-400" />
                <span className="text-sm font-bold text-gray-300 uppercase tracking-wider">Países</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {paises.map((p, i) => (
                  <span key={i} className="px-4 py-1.5 text-sm font-semibold text-white rounded-full" style={{ backgroundColor: '#F4A261' }}>{p}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TalentModal;
